import React, { useEffect, useMemo, useState } from 'react';
import { getSuperAdminTenantHealth } from '../services/api';
import { Tenant, TenantHealth, TenantHealthStatus } from '../types';

type TenantHealthProps = {
  tenants: Tenant[];
  onInspectTenant: (tenant: Tenant) => void;
  onImpersonateTenant: (tenant: Tenant) => void;
  refreshSignal: number;
};

const HEALTH_STATUS_COPY: Record<TenantHealthStatus, string> = {
  healthy: 'Healthy',
  at_risk: 'At risk',
  critical: 'Critical',
};

const HEALTH_STATUS_FILTERS: Array<{ value: TenantHealthStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'critical', label: 'Critical' },
  { value: 'at_risk', label: 'At risk' },
  { value: 'healthy', label: 'Healthy' },
];

export default function SuperAdminTenantHealth({
  tenants,
  onInspectTenant,
  onImpersonateTenant,
  refreshSignal,
}: TenantHealthProps) {
  const [healthRows, setHealthRows] = useState<TenantHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantHealthStatus | ''>('');
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    loadTenantHealth();
  }, [refreshSignal]);

  useEffect(() => {
    if (healthRows.length === 0) {
      setSelectedId('');
      return;
    }
    if (!selectedId || !healthRows.some((row) => row.id === selectedId)) {
      setSelectedId(healthRows[0].id);
    }
  }, [healthRows, selectedId]);

  async function loadTenantHealth() {
    setLoading(true);
    setError('');
    try {
      const rows = await getSuperAdminTenantHealth();
      setHealthRows(rows);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load tenant health.');
    } finally {
      setLoading(false);
    }
  }

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return healthRows.filter((row) => {
      const matchesStatus = !statusFilter || row.health_status === statusFilter;
      const matchesSearch = !needle
        || row.name.toLowerCase().includes(needle)
        || row.id.toLowerCase().includes(needle)
        || row.effective_plan.toLowerCase().includes(needle)
        || row.signals.some((signal) => signal.toLowerCase().includes(needle));
      return matchesStatus && matchesSearch;
    });
  }, [healthRows, search, statusFilter]);

  const selectedHealth = filteredRows.find((row) => row.id === selectedId) || filteredRows[0] || healthRows[0] || null;
  const selectedTenant = selectedHealth ? tenants.find((tenant) => tenant.id === selectedHealth.id) : undefined;
  const criticalCount = healthRows.filter((row) => row.health_status === 'critical').length;
  const atRiskCount = healthRows.filter((row) => row.health_status === 'at_risk').length;
  const averageScore = healthRows.length
    ? Math.round(healthRows.reduce((sum, row) => sum + row.health_score, 0) / healthRows.length)
    : 0;
  const staleDataCount = healthRows.filter((row) => !row.has_data_24h).length;

  return (
    <section className="sd-page-stack sd-tenant-health-page sd-tenant-health-command-page">
      <section className="sd-command-tab-summary sd-tenant-health-summary">
        <div className="sd-command-tab-copy">
          <span>Tenant Health</span>
          <h2>Health Matrix</h2>
          <p>Worst-first customer signals across telemetry freshness, device state, usage, security, and support load.</p>
        </div>
        <div className="sd-command-micro-metrics" aria-label="Tenant health summary">
          <TenantHealthStat label="Critical" value={formatNumber(criticalCount)} detail="Needs intervention" tone="critical" compact />
          <TenantHealthStat label="At risk" value={formatNumber(atRiskCount)} detail="Watch closely" tone="warning" compact />
          <TenantHealthStat label="Stale data" value={formatNumber(staleDataCount)} detail="No data in 24h" tone="warning" compact />
          <TenantHealthStat label="Average" value={`${averageScore}`} detail="Score out of 100" tone="blue" compact />
        </div>
      </section>

      {error && (
        <div className="sd-inline-alert is-danger">
          <span>{error}</span>
          <button onClick={loadTenantHealth} type="button">Retry</button>
        </div>
      )}

      <div className="sd-tenant-health-matrix-layout">
        <div className="sd-tenant-health-main-column">
          <aside className="sd-panel sd-tenant-health-filter-rail">
            <div className="sd-command-panel-header">
              <div>
                <h2>Filters</h2>
                <p>{filteredRows.length} of {healthRows.length} tenants shown.</p>
              </div>
            </div>
            <div className="sd-tenant-health-filter-stack">
              <label className="sd-field">
                <span>Status</span>
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as TenantHealthStatus | '')}>
                  {HEALTH_STATUS_FILTERS.map((item) => (
                    <option key={item.value || 'all'} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <small>Computed customer health band.</small>
              </label>
              <label className="sd-field">
                <span>Search</span>
                <input
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="tenant, plan, signal, or id..."
                  type="search"
                  value={search}
                />
                <small>Matches names, plans, IDs, and signal text.</small>
              </label>
              <button className="sd-secondary-button" disabled={loading} onClick={loadTenantHealth} type="button">
                {loading ? 'Refreshing...' : 'Refresh Health'}
              </button>
            </div>

          </aside>

          <article className="sd-panel sd-tenant-health-matrix-panel">
            <div className="sd-command-panel-header">
              <div>
                <h2>Tenant Matrix</h2>
                <p>Click a row to inspect full risk context.</p>
              </div>
              <span className="sd-command-ticket-number">{loading ? 'SYNC' : `${filteredRows.length} rows`}</span>
            </div>

            <div className="sd-table-wrap sd-tenant-health-matrix-wrap">
              <table className="sd-table sd-tenant-health-matrix-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Data</th>
                    <th>Devices</th>
                    <th>Usage</th>
                    <th>Security</th>
                    <th>Tickets</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && healthRows.length === 0 ? (
                    <tr><td className="sd-empty-row" colSpan={7}>Loading tenant health...</td></tr>
                  ) : filteredRows.length === 0 ? (
                    <tr><td className="sd-empty-row" colSpan={7}>No tenants match this filter.</td></tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr
                        className={`is-${row.health_status} ${selectedHealth?.id === row.id ? 'is-selected' : ''}`}
                        key={row.id}
                        onClick={() => setSelectedId(row.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedId(row.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <td>
                          <strong>{row.name}</strong>
                          <span className="sd-mono">{row.id.slice(0, 8)}</span>
                          <span className="sd-tenant-health-row-meta">
                            <TenantHealthBadge status={row.health_status} />
                            {row.is_trial && <span>{row.trial_days_remaining}d trial</span>}
                          </span>
                        </td>
                        <td><HealthSignalCell signal={getDataSignal(row)} /></td>
                        <td><HealthSignalCell signal={getDeviceSignal(row)} /></td>
                        <td><HealthSignalCell signal={getUsageSignal(row)} /></td>
                        <td><HealthSignalCell signal={getSecuritySignal(row)} /></td>
                        <td><HealthSignalCell signal={getTicketSignal(row)} /></td>
                        <td><TenantHealthScore score={row.health_score} status={row.health_status} compact /></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </div>

        <article className="sd-panel sd-tenant-health-detail-panel">
          {selectedHealth ? (
            <>
              <header className="sd-tenant-health-detail-header">
                <div>
                  <span className="sd-command-ticket-number">{selectedHealth.id.slice(0, 8)}</span>
                  <h2>{selectedHealth.name}</h2>
                  <div className="sd-tenant-health-detail-badges">
                    <TenantHealthBadge status={selectedHealth.health_status} />
                    <span className={`sd-state-chip ${selectedHealth.has_data_24h ? 'is-resolved' : 'is-open'}`}>
                      {selectedHealth.has_data_24h ? 'Data fresh' : 'Data stale'}
                    </span>
                    {selectedHealth.is_trial && (
                      <span className="sd-state-chip is-warning">{selectedHealth.trial_days_remaining}d trial</span>
                    )}
                  </div>
                </div>
                <TenantHealthScore score={selectedHealth.health_score} status={selectedHealth.health_status} />
              </header>

              <div className="sd-tenant-health-detail-grid">
                <TenantHealthFact label="Effective plan" value={formatPlanLabel(selectedHealth.effective_plan)} />
                <TenantHealthFact label="Created" value={formatDate(selectedHealth.created_at)} />
                <TenantHealthFact label="Members" value={formatNumber(selectedHealth.member_count)} />
                <TenantHealthFact label="Active users 7d" value={formatNumber(selectedHealth.active_users_7d)} />
                <TenantHealthFact label="Devices" value={`${formatNumber(selectedHealth.device_count)} total`} detail={`${formatNumber(selectedHealth.devices_offline)} offline`} />
                <TenantHealthFact label="Last reading" value={formatDateTime(selectedHealth.last_reading_at || undefined)} />
                <TenantHealthFact label="Dashboards" value={formatNumber(selectedHealth.user_dashboards)} />
                <TenantHealthFact label="Alerts" value={formatNumber(selectedHealth.alert_count)} />
                <TenantHealthFact label="Failed logins 7d" value={formatNumber(selectedHealth.failed_logins_7d)} />
                <TenantHealthFact label="Open tickets" value={formatNumber(selectedHealth.open_tickets)} />
                <TenantHealthFact label="Last login" value={formatDateTime(selectedHealth.last_login_at || undefined)} />
                <TenantHealthFact label="Trial" value={selectedHealth.is_trial ? `${selectedHealth.trial_days_remaining} days left` : 'No active trial'} />
              </div>

              <section className="sd-tenant-health-signals">
                <div className="sd-command-panel-header">
                  <div>
                    <h2>Risk Signals</h2>
                    <p>{selectedHealth.signals.length || 'No'} deduction signals fired.</p>
                  </div>
                </div>
                {selectedHealth.signals.length === 0 ? (
                  <div className="sd-tenant-health-clear-state">No active deductions for this tenant.</div>
                ) : (
                  <div className="sd-tenant-health-signal-list">
                    {selectedHealth.signals.map((signal) => (
                      <div className="sd-tenant-health-signal-row" key={signal}>
                        <span>{getSignalInitial(signal)}</span>
                        <strong>{signal}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="sd-tenant-health-actions">
                <button
                  className="sd-secondary-button"
                  disabled={!selectedTenant}
                  onClick={() => selectedTenant && onInspectTenant(selectedTenant)}
                  type="button"
                >
                  Tenant Details
                </button>
                <button
                  className="sd-primary-button"
                  disabled={!selectedTenant}
                  onClick={() => selectedTenant && onImpersonateTenant(selectedTenant)}
                  type="button"
                >
                  Login as Tenant
                </button>
              </div>
            </>
          ) : (
            <TenantHealthEmpty title="No tenant selected" detail="Choose a tenant from the health queue to inspect its customer signals." />
          )}
        </article>
      </div>
    </section>
  );
}

function TenantHealthStat({
  compact,
  detail,
  label,
  tone,
  value,
}: {
  compact?: boolean;
  detail: string;
  label: string;
  tone: 'blue' | 'critical' | 'healthy' | 'warning';
  value: string;
}) {
  return (
    <div className={`sd-tenant-health-stat is-${tone} ${compact ? 'is-compact' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function TenantHealthScore({
  compact,
  score,
  status,
}: {
  compact?: boolean;
  score: number;
  status: TenantHealthStatus;
}) {
  return (
    <span
      className={`sd-tenant-health-score is-${status} ${compact ? 'is-compact' : ''}`}
      style={{ '--score-deg': `${Math.max(0, Math.min(score, 100)) * 3.6}deg` } as React.CSSProperties}
    >
      <b>{score}</b>
      {!compact && <small>/100</small>}
    </span>
  );
}

function TenantHealthBadge({ status }: { status: TenantHealthStatus }) {
  return (
    <span className={`sd-tenant-health-badge is-${status}`}>
      {HEALTH_STATUS_COPY[status]}
    </span>
  );
}

function TenantHealthFact({ detail, label, value }: { detail?: string; label: string; value: string }) {
  return (
    <div className="sd-tenant-health-fact">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function TenantHealthEmpty({ detail, title }: { detail: string; title: string }) {
  return (
    <div className="sd-command-empty">
      <div>
        <strong>{title}</strong>
        <p>{detail}</p>
      </div>
    </div>
  );
}

type HealthMatrixSignalTone = 'critical' | 'healthy' | 'neutral' | 'warning';

type HealthMatrixSignal = {
  detail: string;
  label: string;
  tone: HealthMatrixSignalTone;
};

function HealthSignalCell({ signal }: { signal: HealthMatrixSignal }) {
  return (
    <span className={`sd-health-matrix-cell is-${signal.tone}`}>
      <i />
      <span>
        <b>{signal.label}</b>
        <small>{signal.detail}</small>
      </span>
    </span>
  );
}

function getDataSignal(row: TenantHealth): HealthMatrixSignal {
  if (row.has_data_24h) {
    return { detail: '24h telemetry', label: 'Fresh', tone: 'healthy' };
  }
  return { detail: 'No data in 24h', label: 'Stale', tone: row.health_status === 'critical' ? 'critical' : 'warning' };
}

function getDeviceSignal(row: TenantHealth): HealthMatrixSignal {
  if (row.device_count === 0) {
    return { detail: 'Setup gap', label: 'No devices', tone: 'warning' };
  }
  if (row.devices_offline === 0) {
    return { detail: `${formatNumber(row.device_count)} assets`, label: 'Online', tone: 'healthy' };
  }
  const offlineRatio = row.devices_offline / row.device_count;
  return {
    detail: `${formatNumber(row.device_count)} assets`,
    label: `${formatNumber(row.devices_offline)} offline`,
    tone: offlineRatio >= 0.5 ? 'critical' : 'warning',
  };
}

function getUsageSignal(row: TenantHealth): HealthMatrixSignal {
  if (row.active_users_7d > 0) {
    return { detail: `${formatNumber(row.active_users_7d)} active 7d`, label: 'Active', tone: 'healthy' };
  }
  return { detail: `${formatNumber(row.member_count)} members`, label: 'Quiet', tone: 'warning' };
}

function getSecuritySignal(row: TenantHealth): HealthMatrixSignal {
  if (row.failed_logins_7d >= 5) {
    return { detail: 'Failed logins 7d', label: `${formatNumber(row.failed_logins_7d)} failed`, tone: 'critical' };
  }
  if (row.failed_logins_7d > 0) {
    return { detail: 'Failed logins 7d', label: `${formatNumber(row.failed_logins_7d)} failed`, tone: 'warning' };
  }
  return { detail: 'No failures 7d', label: 'Clear', tone: 'healthy' };
}

function getTicketSignal(row: TenantHealth): HealthMatrixSignal {
  if (row.open_tickets >= 5) {
    return { detail: 'Support load', label: `${formatNumber(row.open_tickets)} open`, tone: 'critical' };
  }
  if (row.open_tickets > 0) {
    return { detail: 'Support load', label: `${formatNumber(row.open_tickets)} open`, tone: 'warning' };
  }
  return { detail: 'No open tickets', label: 'Clear', tone: 'healthy' };
}

function formatPlanLabel(value?: string | null) {
  if (!value) return 'Unknown';
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatNumber(value: number) {
  return value.toLocaleString();
}

function formatDate(value?: string) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value?: string) {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function getSignalInitial(signal: string) {
  const normalized = signal.trim();
  return normalized ? normalized.slice(0, 1).toUpperCase() : '!';
}
